// PE-54: the WEEK-CLONE actions (the "⧉ Clona settimana" copy and "add a
// week that repeats week N"). Split out of programEditActions.ts, which
// this changeset pushed past the 500-line cap: the same kind of pure
// extraction programEditActionsNote.ts and programEditActionsGroup.ts
// already made off that file, no behaviour change, no caller touched
// (both re-export through the @heraion/shared barrel).
import { setCycleWeeks } from "./programEditActions.js";
import type { Draft } from "./programEdit.js";
import { roundTypeAt, setRoundType } from "./programRoundTypes.js";

// ⧉ Clona settimana (Antenore 2026-06-11, the 5/3/1 flow: fill W1
// vertically across the days, clone it onto W2..Wn, then ramp each week
// with the per-week edit). A pure column copy on the Draft: every slot
// active in `fromWeek` gets its cells/notes copied onto each target
// week. Refuses (never half-clones) when ANY slot already has content
// in a target week (the predictable rule; remove or edit that first).
export type CloneWeekResult =
  | { ok: true; draft: Draft }
  | { ok: false; reason: "empty" }
  | {
      ok: false;
      reason: "conflict";
      conflict: { exerciseId: string; dayIdx: number; week: number };
    };

export const cloneWeekInDraft = (
  draft: Draft,
  fromWeek: number,
  toWeeks: number[],
): CloneWeekResult => {
  const targets = [...new Set(toWeeks)]
    .filter((w) => w !== fromWeek && w >= 0 && w < draft.cycleWeeks)
    .sort((a, b) => a - b);
  const anySource = draft.sessions.some((s) =>
    s.slots.some((slot) => !slot.inactiveWeeks[fromWeek]),
  );
  if (targets.length === 0 || !anySource) return { ok: false, reason: "empty" };

  for (const [dayIdx, session] of draft.sessions.entries()) {
    for (const slot of session.slots) {
      for (const w of targets) {
        if (!slot.inactiveWeeks[w]) {
          return {
            ok: false,
            reason: "conflict",
            conflict: { exerciseId: slot.exerciseId, dayIdx, week: w },
          };
        }
      }
    }
  }

  return {
    ok: true,
    draft: {
      ...draft,
      sessions: draft.sessions.map((session) => ({
        ...session,
        slots: session.slots.map((slot) => {
          if (slot.inactiveWeeks[fromWeek]) return slot;
          const inactiveWeeks = [...slot.inactiveWeeks];
          for (const w of targets) inactiveWeeks[w] = false;
          return {
            ...slot,
            inactiveWeeks,
            setRows: slot.setRows.map((row) => {
              const cells = [...row.cells];
              const notesPerCell = [...row.notesPerCell];
              // PE-54: stays aligned with notesPerCell. A copied column
              // carries its raw translation companion too, or the clone
              // silently loses the coach's authored text.
              const notesRawPerCell = row.notesRawPerCell
                ? [...row.notesRawPerCell]
                : undefined;
              for (const w of targets) {
                cells[w] = { ...row.cells[fromWeek]! };
                notesPerCell[w] = row.notesPerCell[fromWeek] ?? "";
                // Not rawForReplacedText: text and raw move together as an
                // atomic pair from the SAME source week, not a foreign text
                // overwriting a cell whose old raw would become a lie.
                // rawForReplacedText compares against this cell's own prior
                // text and would drop the correct raw almost every time.
                if (notesRawPerCell) {
                  notesRawPerCell[w] = row.notesRawPerCell![fromWeek];
                }
              }
              return {
                ...row,
                cells,
                notesPerCell,
                ...(notesRawPerCell ? { notesRawPerCell } : {}),
              };
            }),
          };
        }),
      })),
    },
  };
};

// PE-45: "add a week that repeats week N", as ONE pure transform.
//
// ⛔ It exists as a composite instead of two calls in the hook because the two
// steps are not independent: the second reads the draft the first produced, and
// a hook that fired `setCycleWeeks` and then `cloneWeek` would hand the second
// the draft from BEFORE the growth (both bind over a closed-over `draft`). One
// updater, one write, no stale read.
//
// The growth is deliberately "blank": a cloned week is produced by
// `cloneWeekInDraft`, and that function refuses to write into a week where any
// slot is already active: a "clone"-grown week is active everywhere, so it
// would report a conflict against the copy it is about to make.
export const addWeekCloning = (draft: Draft, fromWeek: number): CloneWeekResult => {
  const toWeek = draft.cycleWeeks;
  const grown = setCycleWeeks(draft, toWeek + 1, "blank");
  // The cycle is capped (MAX_CYCLE_WEEKS): at the ceiling nothing grew, and
  // there is no week to copy into. "empty" is the honest reason: the caller
  // shows the same "nothing to do" it shows for an empty source.
  if (grown.cycleWeeks !== toWeek + 1) return { ok: false, reason: "empty" };
  const cloned = cloneWeekInDraft(grown, fromWeek, [toWeek]);
  // #336 S2: the repeated round keeps the source's type, as duplicate does.
  return cloned.ok
    ? { ok: true, draft: setRoundType(cloned.draft, toWeek, roundTypeAt(draft, fromWeek)) }
    : cloned;
};
